import { Button } from "@progress/kendo-react-buttons";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ErrorMessage } from "@/components/Dialog/Error";
import ToolTip from "@/components/Kendo/ToolTip";
import { FormApiProvider } from "@/context/FormApiContext";
import { useWizardDispatch, useWizardState } from "./context/WizardContext";
import SlidePanel from "./SlidePanel";
import { type WizardSlidePanelProps } from "./types";
import { useFormApi } from "./useFormApi";
import { useResetWizardOnOpen } from "./useResetWizardOnOpen";
import { extractApiErrorMessage, hasDisplayableError } from "./utils";
import {
  flattenErrorPaths,
  validateBeforeSubmit,
} from "./validateBeforeSubmit";
import WizardPreviewColumn from "./WizardPreviewColumn";
import { WizardProgressDots } from "./WizardProgressDots";
import { resolveWizardStepAccess } from "./wizardStepAccess";
import { WizardStepNav } from "./WizardStepNav";

export function WizardInner<T>({
  className = "",
  open,
  onClose,
  title,
  subtitle,
  size = "md",
  items,
  isEdit,
  headerActions,
  summaryContent,
  onComplete,
  onSaveDraft,
  navGroupedRight = false,
  onBeforeNext,
  isLoading = false,
  isSubmitting = false,
  saveText,
  nextText,
  errorMessage,
  completedSteps,
  stepDependencies,
  onStepChange,
  lockBackNavigation = false,
  stepNavMode = "backwardOnly",
  extraFooterActions,
  onFinishOverride,
  onNextOverride,
  finishDisabled = false,
  finishDisabledTitle,
  nextDisabled = false,
  nextDisabledTitle,
  isFreemium,
  setFormRef,
  validate,
  initialStep = 0,
  previewPanel,
  testId,
}: WizardSlidePanelProps<T>) {
  const { t } = useTranslation();
  const resolvedSaveText = saveText ?? t("slidePanel.finish");
  const resolvedNextText = nextText ?? t("slidePanel.continue");

  const validInitialStep = Math.max(0, Math.min(initialStep, items.length - 1));
  useResetWizardOnOpen(open, validInitialStep);

  const handleComplete = useCallback(
    (values: Record<string, unknown>) => onComplete(values as T),
    [onComplete],
  );

  const formApi = useFormApi<Record<string, unknown>>(handleComplete);
  const methods = useFormContext();

  useEffect(() => {
    setFormRef?.(formApi);
  }, [formApi, setFormRef]);

  const { currentStep, stepValidity } = useWizardState();
  const dispatch = useWizardDispatch();

  const [isAdvancing, setIsAdvancing] = useState(false);
  // Whether the user has attempted to leave this step at least once. Gates the
  // banner below so it never shows before a first Continue click — without it
  // a failed Continue is an unexplained no-op (the button stays enabled by
  // design, see the comment on `nextTooltip` below).
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [previewCollapsed, setPreviewCollapsed] = useState(
    previewPanel?.defaultCollapsed ?? false,
  );
  const stepTitleRef = useRef<HTMLHeadingElement>(null);

  const totalSteps = items.length;
  const isLastStep = currentStep === totalSteps - 1;
  const isCurrentStepValid = stepValidity[currentStep] !== false;
  const currentKey = items[currentStep]?.key ?? String(currentStep);

  // Keep a ref to the latest validate so the validation handler below reads
  // the current closure without re-creating the callback on every render.
  const validateRef = useRef(validate);
  useEffect(() => {
    validateRef.current = validate;
  });

  useEffect(() => {
    onStepChange?.(currentStep, currentKey);
  }, [currentStep, currentKey, onStepChange]);

  // A new step starts with a clean slate — the previous step's failures say
  // nothing about this one.
  useEffect(() => {
    setValidationAttempted(false);
  }, [currentStep]);

  // Shift focus to the step title on step change for screen-reader announcement.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      stepTitleRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [currentStep]);

  // Validate the current step on demand (i.e., when the user clicks Next or
  // Submit). Combines RHF field-level validation (via trigger) with the
  // form-level validate prop. On failure, surfaces form-level errors as field
  // errors so they render inline, and focuses the first invalid field.
  const validateCurrentStep = useCallback(async (): Promise<boolean> => {
    const valid = await validateBeforeSubmit(
      methods,
      validateRef.current as
        | ((
            values: Record<string, unknown>,
          ) => Record<string, string | undefined>)
        | undefined,
      () => setValidationAttempted(true),
    );
    if (valid) setValidationAttempted(false);
    return valid;
  }, [methods]);

  const goNext = useCallback(async () => {
    if (isLastStep) return;

    const valid = await validateCurrentStep();
    if (!valid) return;

    dispatch({ type: "SET_STEP_VALID", step: currentStep, valid: true });

    if (onBeforeNext) {
      setIsAdvancing(true);
      try {
        await onBeforeNext(currentStep, currentKey);
        dispatch({ type: "SET_STEP", step: currentStep + 1 });
      } catch {
        // onBeforeNext rejected — stay on current step; error banner will show.
      } finally {
        setIsAdvancing(false);
      }
    } else {
      dispatch({ type: "SET_STEP", step: currentStep + 1 });
    }
  }, [
    isLastStep,
    currentStep,
    currentKey,
    dispatch,
    onBeforeNext,
    validateCurrentStep,
  ]);

  const handleFinish = useCallback(
    async (event?: React.SyntheticEvent) => {
      const valid = await validateCurrentStep();
      if (!valid) return;

      dispatch({ type: "SET_STEP_VALID", step: currentStep, valid: true });

      if (onFinishOverride) {
        await onFinishOverride();
      } else {
        formApi.onSubmit(event);
      }
    },
    [validateCurrentStep, dispatch, currentStep, onFinishOverride, formApi],
  );

  const goPrev = useCallback(() => {
    if (currentStep > 0) dispatch({ type: "SET_STEP", step: currentStep - 1 });
  }, [currentStep, dispatch]);

  // Guards the async window a validated forward jump opens: without it a second
  // click landing mid-validation would dispatch a second SET_STEP from a
  // decision the first click already owns. A ref, not state — it has to be
  // readable and writable within the click's own tick, and it must not re-render.
  const navJumpInFlightRef = useRef(false);

  /**
   * Enter `index` from the sidebar (or via `goToStep`).
   *
   * Deliberately `void`-returning even though a validated forward jump awaits:
   * every caller — the sidebar's onClick, and `goToStep` on the footer, preview
   * and step-component contexts — is fire-and-forget, and widening the public
   * signature to `Promise<void>` would push a promise onto consumers that have
   * nothing to do with it (and would stop the `goToStep: () => {}` stubs in the
   * component specs from type-checking).
   */
  const jumpToStep = useCallback(
    (index: number) => {
      // Clicking the active step is a no-op. Worth an early return rather than
      // re-dispatching the same index, which would re-render for nothing.
      if (index === currentStep) return;

      const step = items[index];
      if (!step) return;

      const access = resolveWizardStepAccess({
        index,
        step,
        currentStep,
        stepValidity,
        completedSteps,
        stepDependencies,
        lockBackNavigation,
        navMode: stepNavMode,
      });
      if (!access.canEnter) return;

      if (!access.requiresValidation) {
        dispatch({ type: "SET_STEP", step: index });
        return;
      }

      // A forward jump runs the same gate as the footer's Continue, so leaving a
      // step forward can never skip its validation. `onBeforeNext` is
      // deliberately NOT invoked: a jump can skip steps, and each consumer's
      // `onBeforeNext` reads live state that only exists while its own step is
      // mounted. Steps whose entry *is* the side effect declare
      // `enterViaFooterOnly` instead.
      if (navJumpInFlightRef.current) return;
      navJumpInFlightRef.current = true;
      void validateCurrentStep()
        .then(valid => {
          // Staying put is the whole point on failure: `validateBeforeSubmit`
          // has already flagged the fields and focused the first one, and the
          // step never changed, so the validation banner survives.
          if (!valid) return;
          // The panel closed while we were validating. `SlidePanel` keeps this
          // subtree mounted for the exit transition, so dispatching now would
          // visibly change the step mid-slide-out.
          if (!open) return;
          dispatch({ type: "SET_STEP_VALID", step: currentStep, valid: true });
          dispatch({ type: "SET_STEP", step: index });
        })
        // A field-level validator that throws would otherwise surface as an
        // unhandled rejection. Swallowing it leaves the user on the current
        // step, which is the same outcome as failing validation.
        .catch(() => {})
        .finally(() => {
          navJumpInFlightRef.current = false;
        });
    },
    [
      completedSteps,
      currentStep,
      dispatch,
      items,
      lockBackNavigation,
      open,
      stepDependencies,
      stepNavMode,
      stepValidity,
      validateCurrentStep,
    ],
  );

  // Derived from live form state, so the banner clears as the user fixes each
  // field rather than going stale on a count cached at the last Continue click.
  // Note this is the whole form's error tree: WizardSlidePanel leaves RHF's
  // `shouldUnregister` at false, so fields on already-visited steps stay
  // registered and trigger() spans them. Hence the step-agnostic copy.
  const { errors: liveErrors } = useFormState({ control: methods.control });
  const invalidFieldCount = validationAttempted
    ? flattenErrorPaths(liveErrors).length
    : 0;

  const isDirty = Object.keys(formApi.modified).length > 0;
  const loading = isLoading || isSubmitting || isAdvancing;
  const StepComponent = items[currentStep]?.Component;

  // Shared context for the footer render-prop AND the preview render-prop, so
  // the two can never diverge.
  const footerCtxBase = {
    currentStep,
    isCurrentStepValid,
    isLastStep,
    loading,
    totalSteps,
    formApi,
    goToStep: jumpToStep,
    setStep: (step: number) => dispatch({ type: "SET_STEP", step }),
  };

  const renderedExtraFooterActions =
    typeof extraFooterActions === "function"
      ? extraFooterActions(footerCtxBase)
      : extraFooterActions;

  // Preview column (opt-in, size="full" only). Hidden — not unmounted — on
  // excluded steps so collapse state and scroll position survive navigation.
  const previewVisible =
    !!previewPanel &&
    size === "full" &&
    (previewPanel.visibleOnSteps
      ? previewPanel.visibleOnSteps.includes(currentKey)
      : true);

  const previewNode =
    previewPanel && size === "full" ? (
      <WizardPreviewColumn
        title={previewPanel.title}
        collapsed={previewCollapsed}
        onToggle={() => setPreviewCollapsed(c => !c)}
        hidden={!previewVisible}
        width={previewPanel.width ?? 320}
      >
        {/* Skip the render-prop while collapsed: the column shows the rail (not
            children) in that state, so invoking it on every keystroke would be
            wasted work for heavier previews. */}
        {previewCollapsed
          ? null
          : previewPanel.render({ ...footerCtxBase, currentKey })}
      </WizardPreviewColumn>
    ) : null;

  const apiErrorBanner = hasDisplayableError(errorMessage) ? (
    <ErrorMessage
      message={extractApiErrorMessage(
        errorMessage,
        t("slidePanel.errorFallback"),
      )}
    />
  ) : undefined;

  const validationBanner =
    invalidFieldCount > 0 ? (
      <div data-test-id="wizard-step-validation-error">
        <ErrorMessage
          message={t("slidePanel.validation.requiredSummary", {
            count: invalidFieldCount,
          })}
        />
      </div>
    ) : undefined;

  const errorBanner =
    apiErrorBanner || validationBanner ? (
      <>
        {apiErrorBanner}
        {validationBanner}
      </>
    ) : undefined;

  const saveDraftButton = onSaveDraft ? (
    <Button
      type="button"
      fillMode="link"
      onClick={() => onSaveDraft(methods.getValues() as T)}
      disabled={loading}
      size="medium"
      data-test-id="wizard-save-draft-btn"
    >
      {t("slidePanel.saveAsDraft")}
    </Button>
  ) : null;

  const prevButton = (
    <Button
      size="medium"
      type="button"
      onClick={goPrev}
      disabled={currentStep === 0 || loading || lockBackNavigation}
      className="tlx-panel-wizard-prev-btn"
      data-test-id="wizard-prev-btn"
    >
      <ChevronLeft size={16} />
      {t("slidePanel.previous")}
    </Button>
  );

  // An override deliberately bypasses form validation, so step validity stops
  // gating the button once one is supplied — a "Close"-style override has to
  // stay clickable on a step the user never completed.
  const hasPrimaryOverride = isLastStep ? !!onFinishOverride : !!onNextOverride;

  const isNextDisabled =
    loading ||
    // `finishDisabled` gates Finish on its own, whether or not the consumer
    // also overrides the finish action.
    (isLastStep && finishDisabled) ||
    // `nextDisabled` is its non-final-step counterpart.
    (!isLastStep && nextDisabled) ||
    (hasPrimaryOverride ? false : !isCurrentStepValid);

  // The tooltip explains `finishDisabled` / `nextDisabled` specifically — never
  // the ordinary step-invalid case, whose reasons the form already reports
  // inline.
  const nextTooltip =
    (isLastStep
      ? finishDisabled && finishDisabledTitle
      : nextDisabled && nextDisabledTitle) || "";

  const nextButton = (
    <ToolTip
      anchorElement="target"
      title={nextTooltip}
      isFreemium={nextTooltip ? isFreemium : undefined}
    >
      <Button
        type="button"
        themeColor="secondary"
        onClick={
          isLastStep
            ? (e: React.SyntheticEvent) => {
                void handleFinish(e);
              }
            : onNextOverride
              ? () => {
                  void onNextOverride();
                }
              : goNext
        }
        disabled={isNextDisabled}
        icon={loading ? "loading" : undefined}
        size="medium"
        data-test-id="wizard-next-btn"
      >
        {isLastStep ? resolvedSaveText : resolvedNextText}
        {/* An overridden primary action is not the ordinary "advance to the next
            step" — it may well close the panel — so it drops the forward chevron
            rather than promising a move the click will not make. */}
        {!isLastStep && !onNextOverride && <ChevronRight size={16} />}
      </Button>
    </ToolTip>
  );

  // Two footer layouts:
  //  • grouped (opt-in via navGroupedRight): Save as Draft on the left; Previous
  //    (secondary) + Continue (primary) grouped together on the right.
  //  • default: Previous on the left; Save as Draft + Continue on the right.
  const footerActions = navGroupedRight ? (
    <>
      <div className="tlx-panel-footer__actions-left">{saveDraftButton}</div>
      <div className="tlx-panel-footer__actions-right">
        {prevButton}
        {renderedExtraFooterActions}
        {nextButton}
      </div>
    </>
  ) : (
    <>
      <div className="tlx-panel-footer__actions-left">{prevButton}</div>
      <div className="tlx-panel-footer__actions-right">
        {saveDraftButton}
        {renderedExtraFooterActions}
        {nextButton}
      </div>
    </>
  );

  const wizardBody = (
    <div className={`tlx-panel-wizard-layout ${className}`}>
      <WizardStepNav
        items={items}
        currentStep={currentStep}
        stepValidity={stepValidity}
        completedSteps={completedSteps}
        stepDependencies={stepDependencies}
        lockBackNavigation={lockBackNavigation}
        stepNavMode={stepNavMode}
        onStepClick={jumpToStep}
      />

      <div className="tlx-panel-wizard-content">
        <div className="tlx-panel-wizard-content__meta">
          <div>
            <h3
              className="tlx-panel-wizard-content__title"
              ref={stepTitleRef}
              tabIndex={-1}
            >
              {items[currentStep]?.label}
            </h3>
            <p className="tlx-panel-wizard-content__step-label">
              {t("slidePanel.stepLabel", {
                current: currentStep + 1,
                total: totalSteps,
              })}
            </p>
          </div>
          <div className="tlx-panel-wizard-content__meta-aside">
            {items[currentStep]?.headerAside}
            <WizardProgressDots total={totalSteps} current={currentStep} />
          </div>
        </div>

        <div className="tlx-panel-wizard-content__body">
          {StepComponent && (
            <StepComponent
              step={currentStep}
              isEdit={isEdit}
              formApi={formApi}
              goToStep={jumpToStep}
              setStep={step => dispatch({ type: "SET_STEP", step })}
            />
          )}
        </div>
      </div>

      {previewNode}
    </div>
  );

  // Wizard descendants read form state through `useFormApiCtx()` (or react-hook-form
  // directly). There is deliberately no bridge into the legacy `@/context/FormContext`
  // here: it used to exist for the MCPRegistry tree, but every consumer has since moved
  // to `useFormApiCtx`. Re-adding it would make a missing prop injection degrade to
  // *wrong form* instead of *no form* — a shared component defaulting to the legacy
  // `useForm()` would silently bind to the wizard's form (see `ColumnsValue`'s
  // `valueGetter`, which the Domain Policy wizard injects explicitly).
  return (
    <FormApiProvider formApi={formApi} isEdit={isEdit}>
      <SlidePanel
        open={open}
        onClose={onClose}
        title={title}
        subtitle={subtitle}
        size={size}
        isDirty={isDirty}
        isLoading={isLoading}
        className="tlx-panel--wizard"
        headerActions={headerActions}
        summaryContent={summaryContent}
        footerActions={footerActions}
        errorBanner={errorBanner}
        testId={testId}
        disableCloseOnOverlayClick
      >
        {wizardBody}
      </SlidePanel>
    </FormApiProvider>
  );
}
